import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useThemeUI } from "theme-ui";

import { BackButton } from "./styled";

import LoginCard from "components/ui/LoginCard";
import H2 from "components/typography/H2";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import Subtext from "components/typography/Subtext";
import PasswordInput from "components/forms/PasswordInput";
import { BackArrowIcon } from "components/svg";

import { resetPassword, checkPasswordResetToken } from "api/Auth";

const ResetPassword = () => {
  const themeContext = useThemeUI();
  const { theme } = themeContext;
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [validForm, setValidForm] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<any>(null);
  const [responseErrors] = useState<any>(false);

  const _onSubmit = async (values: any) => {
    const token = searchParams.get("token");
    const res = await resetPassword({ token : token?.toString() || "" , password : values.password, passwordConfirm : values.passwordConfirm });
    if(res?.success){
      toast.add(res.message, "#667E4E");
      navigate("/login");
    } else {      
      setValidForm(false);      
      setErrorMessage(res?.message || "Error resetting password.");      
    }    
  };
  
  // min 5 characters, 1 upper case letter, 1 lower case letter, 1 numeric digit.
  const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{5,}$/;
  const ResetSchema = Yup.object().shape({
    password: Yup.string().required("This field is required.").matches(passwordRules, { message: "Please create a stronger password" }),
    passwordConfirm: Yup.string().oneOf([Yup.ref("password"), undefined], "Passwords don't match.").required("Confirm Password is required."),
  });

  useEffect(() => { 
    document.title = process.env.REACT_APP_NAME + " | Reset Password";
    const checkToken = async () => {
      const token = searchParams.get("token");  
      if (!token) {
        setValidForm(false);
        setErrorMessage("Password Reset Link is invalid.");
      } else {
        const res = await checkPasswordResetToken({ token });
        if(res.success){
          setValidForm(true);
        }else{
          setValidForm(false);
          setErrorMessage(res.message);
        }
      }
    };
    checkToken(); 
  }, [searchParams]);

  return (
    <LoginCard>
      {validForm && (
        <>
          <H2 textAlign="center" fontWeight="500" marginBottom="2rem">
            Password Reset
          </H2>
          <P color={theme?.colors?.black} textAlign={"center"} fontFamily={"Poppins"}>
            Enter and confirm your new password.
          </P>
          <Subtext color={theme?.colors?.dkgrey} textAlign="center" fontFamily={"Poppins"}>Passwords must be a minimum of 5 characters, include 1 uppercase letter, 1 lowercase letter, and 1 numeric digit.</Subtext>
        </>
      )}
      {!validForm && errorMessage &&
        <>
          <H2 textAlign="center" fontWeight="500" marginBottom="2rem">
            Password Reset Link Error
          </H2>
          <P color={theme?.colors?.danger} textAlign={"center"} fontWeight={"500"} fontFamily={"Poppins"}>
            {errorMessage}
          </P>
        </>
      }        
      {validForm && (          
        <Formik initialValues={{ password: "", passwordConfirm: "" }} onSubmit={_onSubmit} validationSchema={ResetSchema}>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            return (
              <Form noValidate autoComplete="off">
                <PasswordInput 
                  name="password" 
                  label="New Password" 
                  autoComplete="new-password" 
                  value={values.password || ""}
                  onChange={(val: any) => setFieldValue("password",val)}
                  $errors={errors.password && submitCount > 0 ? errors.password : null} 
                  $responseErrors={responseErrors}
                />
                <PasswordInput
                  name="passwordConfirm"
                  label="Confirm New Password"
                  autoComplete="new-password"
                  value={values.passwordConfirm || ""}
                  onChange={(val: any) => setFieldValue("passwordConfirm",val)}
                  $errors={errors.passwordConfirm && submitCount > 0 ? errors.passwordConfirm : null}
                />
                <BasicButton                        
                  type="submit"
                  $submitting={isSubmitting}
                  $loadingColor="#fff"
                  styles={{width:"100%",margin : "10px auto 0px auto"}}
                  keepText={false}
                >Save New Password</BasicButton>
              </Form>
            );
          }}
        </Formik>
      )}        
      <BackButton>
        <Link to={validForm ? "/login" : "/forgot-password"}>                
          <BackArrowIcon fill={theme?.colors?.body} width={"15px"} height={"auto"} margin={"0 10px 0 0"} />                
          <span>{validForm ? "Back to Login" : "Send New Password Reset"}</span>
        </Link>
      </BackButton>      
    </LoginCard>
  );
};

export default ResetPassword;
